import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../i18n/LanguageContext';
import { api } from '../../services/api';
import type { GarageMemberResponse, MemberNotificationSettings } from '../../types/settings';
import UsernameAvailability from '../../components/auth/UsernameAvailability';
import MemberNotificationControls from './MemberNotificationControls';
import { memberNotifications } from './MemberNotificationBadges';

interface EditTeamMembersModalProps {
  members: GarageMemberResponse[];
  onClose: () => void;
  onSaved: () => void;
  /** Spec 076 — a notification save landed; the Team list updates the row without closing the modal. */
  onMemberUpdated?: (member: GarageMemberResponse) => void;
}

export default function EditTeamMembersModal({ members, onClose, onSaved, onMemberUpdated }: EditTeamMembersModalProps) {
  const { token, user } = useAuth();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<'add' | 'edit'>('add');

  return (
    <div className="settings-modal-overlay">
      <div className={`settings-modal settings-modal-wide${activeTab === 'edit' ? ' settings-modal-team' : ''}`} onClick={e => e.stopPropagation()}>
        <div className="settings-modal-header">
          <h3>{t('settings.editAddMembers')}</h3>
          <button className="settings-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="settings-modal-tabs">
          <button className={activeTab === 'add' ? 'active' : ''} onClick={() => setActiveTab('add')}>
            {t('settings.addNew')}
          </button>
          <button className={activeTab === 'edit' ? 'active' : ''} onClick={() => setActiveTab('edit')}>
            {t('settings.editTeam')}
          </button>
        </div>
        {activeTab === 'add' ? (
          <AddMemberForm token={token} t={t} onSaved={onSaved} />
        ) : (
          <EditMembersForm
            members={members}
            currentUserId={user?.id ?? null}
            token={token}
            t={t}
            onSaved={onSaved}
            onMemberUpdated={onMemberUpdated}
          />
        )}
      </div>
    </div>
  );
}

function AddMemberForm({ token, t, onSaved }: { token: string | null; t: (k: string) => string; onSaved: () => void }) {
  const [username, setUsername] = useState('');
  const [usernameValid, setUsernameValid] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'MANAGER' | 'STAFF'>('STAFF');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !name.trim() || !phone.trim() || !password || !username.trim()) return;

    if (!usernameValid) {
      setError(t('auth.username.notReady'));
      return;
    }
    if (password.length < 6) {
      setError(t('settings.passwordMinChars'));
      return;
    }

    setLoading(true);
    setError('');
    try {
      let normalizedPhone = phone.trim().replace(/^\+/, '');
      if (normalizedPhone.startsWith('0')) {
        normalizedPhone = '971' + normalizedPhone.substring(1);
      } else if (!normalizedPhone.startsWith('971')) {
        normalizedPhone = '971' + normalizedPhone;
      }
      await api.addGarageMember({
        username: username.trim().toLowerCase(),
        name,
        phone: normalizedPhone,
        email,
        password,
        role,
      }, token);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.failedToAddMember'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="settings-modal-form">
      <p className="settings-form-section-title">{t('settings.addNewMember')}</p>
      {error && <div className="settings-toast settings-toast-error">{error}</div>}
      <div className="settings-form-row">
        <input
          value={username}
          onChange={e => { setUsername(e.target.value); setError(''); }}
          placeholder={t('auth.username.placeholder')}
          required
          dir="ltr"
          spellCheck={false}
          autoComplete="off"
        />
        <UsernameAvailability value={username} onValidityChange={setUsernameValid} />
      </div>
      <div className="settings-form-row">
        <input value={name} onChange={e => { setName(e.target.value); setError(''); }} placeholder={t('settings.memberName')} required />
      </div>
      <div className="settings-form-row">
        <div className="phone-input-group">
          <span className="phone-prefix">971</span>
          <input value={phone} onChange={e => { setPhone(e.target.value); setError(''); }} placeholder={t('settings.memberPhone')} type="tel" required />
        </div>
      </div>
      <div className="settings-form-row">
        <input value={email} onChange={e => { setEmail(e.target.value); setError(''); }} placeholder={t('settings.memberEmail')} type="email" />
      </div>
      <div className="settings-form-row">
        <input value={password} onChange={e => { setPassword(e.target.value); setError(''); }} placeholder={t('settings.memberPassword')} type="password" required />
      </div>
      <div className="settings-form-row">
        <select value={role} onChange={e => setRole(e.target.value as 'MANAGER' | 'STAFF')}>
          <option value="MANAGER">{t('settings.roleManager')}</option>
          <option value="STAFF">{t('settings.roleStaff')}</option>
        </select>
      </div>
      <button type="submit" className="settings-btn-primary" disabled={loading}>
        {loading ? t('common.saving') : t('common.saveChanges')}
      </button>
    </form>
  );
}

function EditMembersForm({ members, currentUserId, token, t, onSaved, onMemberUpdated }: {
  members: GarageMemberResponse[];
  currentUserId: string | null;
  token: string | null;
  t: (k: string) => string;
  onSaved: () => void;
  onMemberUpdated?: (member: GarageMemberResponse) => void;
}) {
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  // Spec 076 — the OWNER row is listed too, for its notification settings only (role, active and
  // remove stay locked for the owner, as before).
  const listedMembers = members.filter(m => m.active !== false);
  const me = listedMembers.find(m => m.userSubjectId === currentUserId);
  const notificationsReadOnly = !me || me.role === 'STAFF';
  const [notifications, setNotifications] = useState<Record<string, MemberNotificationSettings>>(
    () => Object.fromEntries(listedMembers.map(m => [m.id, memberNotifications(m)])),
  );

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(''), 3000);
    return () => clearTimeout(timer);
  }, [notice]);

  // Optimistic: the row shows the new values at once and goes back to the previous ones if the save fails.
  const handleNotificationsChange = async (memberId: string, next: MemberNotificationSettings) => {
    if (!token || notificationsReadOnly) return;
    const previous = notifications[memberId];
    setNotifications(prev => ({ ...prev, [memberId]: next }));
    setError('');
    try {
      const updated = await api.updateMemberNotifications(memberId, next, token);
      setNotifications(prev => ({ ...prev, [memberId]: memberNotifications(updated) }));
      onMemberUpdated?.(updated);
      setNotice(t('settings.memberNotifications.saved'));
    } catch (err) {
      setNotifications(prev => ({ ...prev, [memberId]: previous }));
      setNotice('');
      setError(err instanceof Error && err.message ? err.message : t('settings.memberNotifications.saveFailed'));
    }
  };

  const handleRoleChange = async (memberId: string, newRole: 'MANAGER' | 'STAFF') => {
    if (!token) return;
    try {
      await api.updateMemberRole(memberId, { role: newRole }, token);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.failedToUpdateRole'));
    }
  };

  const handleToggleActive = async (memberId: string, currentActive: boolean) => {
    if (!token) return;
    try {
      await api.toggleMemberActivation(memberId, !currentActive, token);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.failedToToggle'));
    }
  };

  const handleRemove = async (memberId: string) => {
    if (!token) return;
    try {
      await api.removeGarageMember(memberId, token);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.failedToRemoveMember'));
    }
  };

  if (listedMembers.length === 0) {
    return <div className="settings-modal-form"><p>{t('settings.noEditableMembers')}</p></div>;
  }

  return (
    <div className="settings-modal-form">
      {error && <div className="settings-toast settings-toast-error" role="alert">{error}</div>}
      {notice && <div className="settings-toast settings-toast-success" role="status">{notice}</div>}
      {notificationsReadOnly && (
        <p id="member-notify-readonly-note" className="member-notify-note">{t('settings.memberNotifications.readOnly')}</p>
      )}
      <div className="settings-members-table-wrap">
      <table className="settings-members-table">
        <thead>
          <tr>
            <th>{t('settings.teamMember')}</th>
            <th>{t('auth.username')}</th>
            <th>{t('settings.role')}</th>
            <th>{t('settings.email')}</th>
            <th>{t('settings.memberNotifications.title')}</th>
            <th></th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {listedMembers.map(member => {
            const isOwner = member.role === 'OWNER';
            return (
              <tr key={member.id} className={!member.active ? 'member-inactive' : ''}>
                <td>{member.name || member.userSubjectId}</td>
                <td>{member.username ? `@${member.username}` : '-'}</td>
                <td>
                  {isOwner ? t('settings.roleOwner') : (
                    <select
                      value={member.role}
                      onChange={e => handleRoleChange(member.id, e.target.value as 'MANAGER' | 'STAFF')}
                    >
                      <option value="MANAGER">{t('settings.roleManager')}</option>
                      <option value="STAFF">{t('settings.roleStaff')}</option>
                    </select>
                  )}
                </td>
                <td>{member.email || '-'}</td>
                <td>
                  <MemberNotificationControls
                    memberId={member.id}
                    memberName={member.name || member.username || member.userSubjectId}
                    value={notifications[member.id] ?? memberNotifications(member)}
                    whatsappDeliverable={member.whatsappDeliverable !== false}
                    emailDeliverable={member.emailDeliverable !== false}
                    readOnly={notificationsReadOnly}
                    onChange={next => handleNotificationsChange(member.id, next)}
                    t={t}
                  />
                </td>
                <td>
                  {!isOwner && (
                    <label className="toggle-switch toggle-switch-sm">
                      <input type="checkbox" checked={member.active} onChange={() => handleToggleActive(member.id, member.active)} />
                      <span className="toggle-slider" />
                    </label>
                  )}
                </td>
                <td>
                  {!isOwner && (
                    <button className="settings-remove-btn" onClick={() => handleRemove(member.id)}>
                      {t('common.remove')}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
}
